import { ResendLinkToParkerExecutor } from './parker-function-executor/resend-link-to-parker-executor.class';
import { UnlinkParkerExecutor } from './parker-function-executor/unlink-parker-executor.class';
import { AddSubscriptionExecutor } from './parker-function-executor/add-subscription-executor.class';
import { AfterViewChecked, ChangeDetectorRef, Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormGroup, Validators, FormBuilder, ValidatorFn, AbstractControl } from '@angular/forms';
import { DialogComponent, DialogModule } from '@syncfusion/ej2-angular-popups';
import { ParkerFunction } from './parker-function';
import { ClientService } from 'src/app/base/services/client/client.service';
import { ListBoxComponent, ListBoxModule } from '@syncfusion/ej2-angular-dropdowns';
import { PagedResultDTOOfParkerDTO, ParkerClient, ParkerRegistrationClient, ParkingClient, ParkingSubscriptionClient, ParkingSubscriptionDTO} from 'src/app/base/models/models.swagger.generated';
import { AnimationModel, ProgressBar, ProgressBarModule } from '@syncfusion/ej2-angular-progressbar';
import { Observable, Subscription} from 'rxjs';
import { GridComponent, GridModule } from '@syncfusion/ej2-angular-grids';
import { IParkerExecutionStatus } from './parker-execution-status';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { CustomValidatorsFactory } from 'src/app/base/shared/validators/custom-validators-factory';
import { FunctionType } from './parker-function.types';
import { EndSubscriptionFormGroup, EndSubscriptionFormType } from './parker-function-end-subscription-form/parker-function-end-subscription-form.type';
import { ReplaceSubscriptionFormGroup, ReplaceSubscriptionFormType } from './parker-function-replace-subscription-form/parker-function-replace-subscription-form.type';
import { SubscriptionAssignmentFormGroup, SubscriptionAssignmentFormType } from '../edit-parker-form/edit-parker-edit-subscription-list-form/edit-parker-edit-subscription-list-form.type';
import { SetStatusExecutor } from './parker-function-executor/set-status-executor.class';
import { ParkerFunctionExecutor } from './parker-function-executor/parker-function-executor.class';
import { EndSubscriptionExecutor } from './parker-function-executor/end-subscription-executor.class';
import { ReplaceSubscriptionExecutor } from './parker-function-executor/replace-subscription-executor.class';
import { ParkerFunctionReplaceSubscriptionFormComponent } from './parker-function-replace-subscription-form/parker-function-replace-subscription-form.component';
import { ParkerFunctionEndSubscriptionFormComponent } from './parker-function-end-subscription-form/parker-function-end-subscription-form.component';
import { EditParkerEditSubscriptionListFormComponent } from '../edit-parker-form/edit-parker-edit-subscription-list-form/edit-parker-edit-subscription-list-form.component';
import { NgIf } from '@angular/common';
import { RecalculateParkingSessionsExecutor } from './parker-function-executor/recalculate-executor.class';
import { ParkerFunctionFromUntilFormComponent } from './parker-function-from-until-form/parker-function-from-until-form.component';
import { FromUntilFormGroup, FromUntilFormType } from './parker-function-from-until-form/parker-function-from-until-form.types';
import { AdjustSubscriptionExecutor } from './parker-function-executor/adjust-subscription-executor.class';
import { AddSubscriptionSetExecutor } from './parker-function-executor/add-subscription-set-executor.class';

@Component({
    selector: 'app-parker-function',
    templateUrl: './parker-function.component.html',
    styleUrls: ['./parker-function.component.scss'],
    imports: [DialogModule, ListBoxModule, NgIf, EditParkerEditSubscriptionListFormComponent, ParkerFunctionEndSubscriptionFormComponent, ParkerFunctionReplaceSubscriptionFormComponent, ParkerFunctionFromUntilFormComponent, ProgressBarModule, GridModule, TranslateModule]
})
export class ParkerFunctionComponent implements OnInit, AfterViewChecked {
  @ViewChild('functionDialog')
  public functionDialog: DialogComponent;

  @ViewChild('functionListBox')
  public functionListBox: ListBoxComponent;

  @ViewChild('functionStatusDialog')
  public functionStatusDialog: DialogComponent;

  @ViewChild('functionStatus')
  public functionStatus: ProgressBar;

  @ViewChild('statusGrid')
  public statusGrid: GridComponent;

  @ViewChild('addSetForm')
  public addSetFormComponent: EditParkerEditSubscriptionListFormComponent;

  @Output() public functionExecuted = new EventEmitter<void>();

  public numTotalRecordsToWorkOn: number = 0;
  public functionForm: UntypedFormGroup
  public functionType = FunctionType;
  public loadParkersPagedCallback: (skip: number, take: number) => Observable<PagedResultDTOOfParkerDTO>;

  public readonly functionList: ParkerFunction[] = [
    { type: FunctionType.AddSubscriptionSet, toolFunction: () => this.addSubscriptionSets() },
    { type: FunctionType.AddSubscriptions, toolFunction: () => this.addSubscriptions() },
    { type: FunctionType.EndSubscriptions, toolFunction: () => this.endSubscriptions() },
    { type: FunctionType.AdjustSubscriptions, toolFunction: () => this.adjustSubscriptions()},
    { type: FunctionType.DeactivateParker, toolFunction: () => this.deactivateParkers() },
    { type: FunctionType.ActivateParker, toolFunction: () => this.activateParkers() },
    { type: FunctionType.UnlinkParker, toolFunction: () => this.unlinkParkers() },
    { type: FunctionType.ResendLink, toolFunction: () => this.resendLinkToParkers() },
    { type: FunctionType.ReplaceSubscription, toolFunction: () => this.replaceSubscription() },
    { type: FunctionType.RecalculateParkingSessions, toolFunction: () => this.recalculateSessions()}
  ];

  public readonly fieldList: object = { text: "type", value: "type" };
  public subscriptionAssignmentForm: SubscriptionAssignmentFormGroup;
  public subscriptionEndingForm: EndSubscriptionFormGroup;
  public subscriptionReplacementForm: ReplaceSubscriptionFormGroup;
  public fromUntilForm: FromUntilFormGroup;

  public readonly animation: AnimationModel = { enable: true, duration: 500, delay: 0 };

  public get progressValue(): number {
    return this._executor?.progressInfo?.progressValue ?? 0;
  }

  public get actualExecutions(): number {
    return this._executor?.progressInfo?.actualIterations ?? 0;
  }

  public get segmentCount(): number {
    return this._executor?.progressInfo?.progressValueSegmentCount ?? 0;
  }

  public get executionStatus(): IParkerExecutionStatus[] {
    return this._executor?.executionStatus;
  }

  public get selectedFunction(): ParkerFunction {
    if (this.functionListBox?.value?.length > 0) {
      return this.functionList.find(f => f.type === this.functionListBox.value[0]);
    }
    return null;
  }

  public get clientId(): number {
    return this.clientService.selectedClient?.id;
  }

  private _executorSubscriptions = new Subscription();
  private _executor: ParkerFunctionExecutor;

  constructor(private clientService: ClientService,
    private fb: FormBuilder,
    private parkingSubscriptionClient: ParkingSubscriptionClient,
    private parkerClient: ParkerClient,
    private parkingClient: ParkingClient,
    private parkerRegistrationClient: ParkerRegistrationClient,
    public translateService: TranslateService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit() {
    this.subscriptionAssignmentForm = this.fb.group<SubscriptionAssignmentFormType>({
      id: this.fb.control<number>(null),
      subscriptionId: this.fb.control<number>(null, [Validators.required]),
      active: this.fb.control<boolean>(true),
      isValidImmediately: this.fb.control<boolean>(true),
      validFrom: this.fb.control<Date>(null),  // dates can be in the ast
      isValidOpenEnd: this.fb.control<boolean>(true),
      validTo: this.fb.control<Date>(null), // dates can be in the past
      modifiedBy: this.fb.control<string>(null),
      modifiedOn: this.fb.control<string>(null),
      selectedSubscriptions: this.fb.control<ParkingSubscriptionDTO[]>(null)
    });

    this.subscriptionEndingForm = this.fb.group<EndSubscriptionFormType>({
      subscriptionId: this.fb.control<number>(null, [Validators.required]),
      validTo: this.fb.control<Date>(null, [CustomValidatorsFactory.validateDateNotInPast()]),
    });

    this.subscriptionReplacementForm = this.fb.group<ReplaceSubscriptionFormType>({
      oldSubscriptionId: this.fb.control<number>(null, [Validators.required]),
      newSubscriptionId: this.fb.control<number>(null, [Validators.required, ParkerFunctionComponent.validateSubscriptionNotSameAsOld()]),
      validFrom: this.fb.control<Date>(null, [CustomValidatorsFactory.validateDateNotInPast()]),
      validTo: this.fb.control<Date>(null, [CustomValidatorsFactory.validateDateNotInPast()]),
      doRecalcSessions: this.fb.control<boolean>(false),
    });
    this.subscriptionReplacementForm.controls.oldSubscriptionId.valueChanges.subscribe(() => {
      this.subscriptionReplacementForm.controls.newSubscriptionId.updateValueAndValidity();
    });

    this.fromUntilForm = this.fb.group<FromUntilFormType>({
      from: this.fb.control<Date>(null, [CustomValidatorsFactory.validateDateNotInFuture()]),
      until: this.fb.control<Date>(null),
    });
    this.fromUntilForm.controls.until.setValidators([CustomValidatorsFactory.validateDateNotInFuture(), CustomValidatorsFactory.validateDateNotBeforeDate(this.fromUntilForm.controls.from)]);
  }

  public ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  public onStatusDialogOpened() {
    // code was placed here to be able to call function status refresh before start executing the function
    this.functionStatus.refresh();
    if (this.numTotalRecordsToWorkOn <= 0 || this._executor == null) {
      this.handleNoData();
      return;
    }

    this._executorSubscriptions = new Subscription();
    this._executorSubscriptions.add(this._executor.executionStatusChanged.subscribe(() => {
      this.statusGrid.refresh();
    }));
    this._executorSubscriptions.add(this._executor.functionExecuted.subscribe(() => {
      this.functionExecuted.emit();
      this._executorSubscriptions.unsubscribe();
    }));
    this._executor.run(this.loadParkersPagedCallback);
  }

  public isTheFunctionSelected(functionName: string): boolean {
    return this.functionListBox?.value?.findIndex(v => v === functionName) >= 0;
  }

  public closeFunctionDialog() {
    this.functionDialog.hide();
  }

  public applyFunction() {
    if (this.selectedFunction) {
      this.selectedFunction.toolFunction();
    }
  }

  public openDialog(numTotalRecordsToWorkOn: number, loadParkersPagedCallback: (skip: number, take: number) => Observable<PagedResultDTOOfParkerDTO>) {
    this.numTotalRecordsToWorkOn = numTotalRecordsToWorkOn;
    this.loadParkersPagedCallback = loadParkersPagedCallback;
    this.functionDialog.show();
  }

  public showFunctionStatusDialog() {
    this.functionStatusDialog.show();
  }

  public closeStatusDialog() {
    this.functionStatusDialog.hide();
    this.functionDialog.hide();
    if (this._executor) {
      this._executor.cancelExecution();
    }
  }

  private handleNoData() {
    this.statusGrid.refresh();
    this.functionStatus.refresh();
  }

  private activateParkers(): void {
    this.setStatusOfParkers(true);
  }

  private deactivateParkers(): void {
    this.setStatusOfParkers(false);
  }

  private setStatusOfParkers(activeStatusToChange: boolean = false) {
    var executor = new SetStatusExecutor(this.numTotalRecordsToWorkOn, activeStatusToChange, this.translateService, this.parkerClient);
    this.runExecutor(executor);
  }

  private addSubscriptionSets() {
    if (this.subscriptionAssignmentForm.valid) {
      var executor = new AddSubscriptionSetExecutor(this.numTotalRecordsToWorkOn, this.subscriptionAssignmentForm, this.parkingSubscriptionClient, this.parkerClient);
      this.runExecutor(executor);
    }
    else {
      this.subscriptionAssignmentForm.markAllAsTouched();
    }
  }

  private addSubscriptions() {
    if (this.subscriptionAssignmentForm.valid) {
      var executor = new AddSubscriptionExecutor(this.numTotalRecordsToWorkOn, this.subscriptionAssignmentForm, this.parkingSubscriptionClient, this.parkerClient);
      this.runExecutor(executor);
    }
    else {
      this.subscriptionAssignmentForm.markAllAsTouched();
    }
  }

  private endSubscriptions(): void {
    if (this.subscriptionEndingForm.valid) {
      var executor = new EndSubscriptionExecutor(this.numTotalRecordsToWorkOn, this.subscriptionEndingForm, this.parkingSubscriptionClient, this.parkerClient, this.translateService);
      this.runExecutor(executor);
    }
    else {
      this.subscriptionEndingForm.markAllAsTouched();
    }
  }

  private replaceSubscription(): void {
    if (this.subscriptionReplacementForm.valid) {
      var executor = new ReplaceSubscriptionExecutor(this.numTotalRecordsToWorkOn, this.subscriptionReplacementForm, this.parkerClient, this.parkingClient, this.parkerRegistrationClient);
      this.runExecutor(executor);
    }
    else {
      this.subscriptionReplacementForm.markAllAsTouched();
    }
  }

  private adjustSubscriptions(): void {
    if (this.subscriptionAssignmentForm.valid) {
      var executor = new AdjustSubscriptionExecutor(this.numTotalRecordsToWorkOn, this.subscriptionAssignmentForm, this.parkingSubscriptionClient, this.parkerClient);
      this.runExecutor(executor);
    }
    else {
      this.subscriptionAssignmentForm.markAllAsTouched();
    }
  }

  private resendLinkToParkers(): void {
    var executor = new ResendLinkToParkerExecutor(this.numTotalRecordsToWorkOn, this.parkerClient, this.translateService);
    this.runExecutor(executor);
  }

  private unlinkParkers(): void {
    var executor = new UnlinkParkerExecutor(this.numTotalRecordsToWorkOn, this.parkerClient, this.translateService);
    this.runExecutor(executor);
  }

  private recalculateSessions(): void {
    if (this.fromUntilForm.valid) {
    var executor = new RecalculateParkingSessionsExecutor(this.numTotalRecordsToWorkOn, this.fromUntilForm, this.parkingClient, this.parkerRegistrationClient);
    this.runExecutor(executor);
    }
    else {
      this.fromUntilForm.markAllAsTouched();
    }
  }

  private runExecutor(executor: ParkerFunctionExecutor) {
    this._executor = executor;
    this.functionStatus.value = this._executor.progressInfo.progressValue;
    this.functionStatus.segmentCount = this._executor.progressInfo.progressValueSegmentCount;
    this.functionStatus.refresh();
    this.showFunctionStatusDialog();
    // run executor is called onStatusDialogOpened to reduce issues with the progress bar visualization :(
  }

  public static validateSubscriptionNotSameAsOld(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.dirty && control.value) {
        const newSubscriptionId = control.value;
        const oldSubscriptionId = control.parent.get('oldSubscriptionId').value;
        return newSubscriptionId === oldSubscriptionId ? {subscriptionIdentical: true} : null;
      } else {
        return null;
      }
    };
  }
}
